import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Send, 
  Loader2, 
  Paperclip, 
  X, 
  Search, 
  ChevronDown, 
  ChevronUp,
  Check,
  CheckCheck,
  FileText,
  Image as ImageIcon
} from 'lucide-react';
import { useDealMessages, DealMessage } from '@/hooks/useDealMessages';
import { useUser } from '@/contexts/UserContext';
import { format, isToday, isYesterday, isSameDay, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { MessageAttachmentsPanel } from './MessageAttachmentsPanel';

interface EnhancedDealMessagingProps {
  dealId: string;
}

interface GroupedMessages {
  date: Date;
  dateLabel: string;
  messages: DealMessage[];
}

const getDateLabel = (date: Date): string => {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMMM d, yyyy');
};

const getInitials = (name: string | undefined): string => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const EnhancedDealMessaging: React.FC<EnhancedDealMessagingProps> = ({ dealId }) => {
  const { messages, loading, sending, sendMessage, markAsRead, uploadFiles, markMessageRead } = useDealMessages(dealId);
  const { currentUser } = useUser();
  const [newMessage, setNewMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState('messages');
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isInitialMount = useRef(true);
  const previousMessageCount = useRef(0);

  // Group messages by day
  const groupedMessages = useMemo((): GroupedMessages[] => {
    const filtered = searchQuery 
      ? messages.filter(m => 
          m.message_text.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.sender?.display_name?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : messages;

    const groups: Map<string, DealMessage[]> = new Map();
    
    filtered.forEach(msg => {
      const date = parseISO(msg.created_at);
      const dateKey = format(date, 'yyyy-MM-dd');
      
      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey)!.push(msg);
    });

    return Array.from(groups.entries())
      .map(([dateKey, msgs]) => ({
        date: parseISO(dateKey),
        dateLabel: getDateLabel(parseISO(msgs[0].created_at)),
        messages: msgs.sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        ),
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [messages, searchQuery]);

  // Get all attachments from messages
  const allAttachments = useMemo(() => {
    return messages.flatMap(msg => {
      if (!msg.file_urls || !Array.isArray(msg.file_urls)) return [];
      return (msg.file_urls as string[]).map(url => ({
        url,
        messageId: msg.id,
        senderName: msg.sender?.display_name || 'Unknown',
        sentAt: msg.created_at,
      }));
    });
  }, [messages]);

  useEffect(() => {
    // Only auto-scroll on initial load or when new messages are added
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousMessageCount.current = messages.length;
      // Scroll to bottom on initial load (instant, not smooth)
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: 'auto' });
      }
      return;
    }
    
    // Only scroll if new messages were added (user sent or received a message)
    if (messages.length > previousMessageCount.current) {
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
    previousMessageCount.current = messages.length;
  }, [messages]);

  useEffect(() => {
    markAsRead();
  }, [markAsRead]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      toast.success(`${files.length} file(s) selected`);
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || sending) return;

    try {
      let fileUrls: string[] = [];
      
      if (selectedFiles.length > 0) {
        toast.loading('Uploading files...');
        fileUrls = await uploadFiles(selectedFiles);
        toast.dismiss();
        toast.success('Files uploaded successfully');
      }
      
      await sendMessage(newMessage || 'Sent files', fileUrls);
      setNewMessage('');
      setSelectedFiles([]);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleDayCollapse = (dateLabel: string) => {
    setCollapsedDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dateLabel)) {
        newSet.delete(dateLabel);
      } else {
        newSet.add(dateLabel);
      }
      return newSet;
    });
  };

  const renderReadReceipt = (message: DealMessage, isOwnMessage: boolean) => {
    if (!isOwnMessage) return null;

    // Message read (double blue check)
    if (message.read_at) {
      return (
        <span className="inline-flex ml-1">
          <CheckCheck className="h-3.5 w-3.5 text-blue-500" />
        </span>
      );
    }
    
    // Message delivered but not read (double gray check)
    if (message.is_read) {
      return (
        <span className="inline-flex ml-1">
          <CheckCheck className="h-3.5 w-3.5 text-muted-foreground" />
        </span>
      );
    }
    
    // Message sent (single gray check)
    return (
      <span className="inline-flex ml-1">
        <Check className="h-3.5 w-3.5 text-muted-foreground" />
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>Deal Messages</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSearch(!showSearch)}
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {showSearch && (
            <div className="mt-3">
              <Input
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-4 pt-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="messages" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Messages
              </TabsTrigger>
              <TabsTrigger value="attachments" className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Attachments ({allAttachments.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="messages" className="mt-4">
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-4">
                  {groupedMessages.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      {searchQuery ? 'No messages match your search.' : 'No messages yet. Start the conversation!'}
                    </div>
                  ) : (
                    groupedMessages.map((group) => (
                      <Collapsible 
                        key={group.dateLabel}
                        open={!collapsedDays.has(group.dateLabel)}
                        onOpenChange={() => toggleDayCollapse(group.dateLabel)}
                      >
                        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm py-2">
                          <CollapsibleTrigger asChild>
                            <button className="w-full flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                              <div className="h-px flex-1 bg-border" />
                              <span className="px-2 font-medium">{group.dateLabel}</span>
                              {collapsedDays.has(group.dateLabel) ? (
                                <ChevronDown className="h-3 w-3" />
                              ) : (
                                <ChevronUp className="h-3 w-3" />
                              )}
                              <div className="h-px flex-1 bg-border" />
                            </button>
                          </CollapsibleTrigger>
                        </div>
                        
                        <CollapsibleContent className="space-y-3">
                          {group.messages.map((message) => {
                            const isOwnMessage = message.sender_profile_id === currentUser?.id;

                            return (
                              <div
                                key={message.id}
                                className={`flex gap-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                              >
                                {/* Avatar for other users */}
                                {!isOwnMessage && (
                                  <Avatar className="h-8 w-8 shrink-0">
                                    <AvatarImage src={message.sender?.avatar_url} />
                                    <AvatarFallback className="text-xs">
                                      {getInitials(message.sender?.display_name)}
                                    </AvatarFallback>
                                  </Avatar>
                                )}
                                
                                <div className={`flex flex-col max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                                  {/* Sender name for others */}
                                  {!isOwnMessage && (
                                    <span className="text-xs text-muted-foreground mb-1 px-1">
                                      {message.sender?.display_name || 'Unknown'}
                                    </span>
                                  )}
                                  
                                  <div
                                    className={`rounded-2xl px-4 py-2.5 shadow-sm ${
                                      isOwnMessage
                                        ? 'bg-primary text-primary-foreground rounded-tr-sm'
                                        : 'bg-muted text-foreground rounded-tl-sm'
                                    }`}
                                  >
                                    {message.message_text && message.message_text !== 'Sent files' && (
                                      <p className="text-sm whitespace-pre-wrap leading-relaxed">
                                        {message.message_text}
                                      </p>
                                    )}
                                    
                                    {message.file_urls && Array.isArray(message.file_urls) && message.file_urls.length > 0 && (
                                      <div className="mt-2 space-y-2">
                                        {(message.file_urls as string[]).map((url: string, index: number) => {
                                          const fileName = url.split('/').pop() || 'file';
                                          const isImage = url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                          
                                          return (
                                            <div key={index}>
                                              {isImage ? (
                                                <a href={url} target="_blank" rel="noopener noreferrer">
                                                  <img 
                                                    src={url} 
                                                    alt={fileName}
                                                    className="max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                                    style={{ maxHeight: '200px' }}
                                                  />
                                                </a>
                                              ) : (
                                                <a
                                                  href={url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className={`flex items-center gap-2 text-sm underline ${
                                                    isOwnMessage ? 'text-primary-foreground/90' : 'text-foreground'
                                                  }`}
                                                >
                                                  <Paperclip className="h-3 w-3" />
                                                  <span className="truncate max-w-[200px]">{fileName}</span>
                                                </a>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Timestamp and read receipt */}
                                  <div className={`flex items-center gap-1 mt-1 px-1 ${isOwnMessage ? 'flex-row-reverse' : ''}`}>
                                    <span className="text-[10px] text-muted-foreground">
                                      {format(parseISO(message.created_at), 'h:mm a')}
                                    </span>
                                    {renderReadReceipt(message, isOwnMessage)}
                                  </div>
                                </div>
                                
                                {/* Avatar for own messages */}
                                {isOwnMessage && (
                                  <Avatar className="h-8 w-8 shrink-0">
                                    <AvatarImage src={currentUser?.avatar_url || ''} />
                                    <AvatarFallback className="text-xs">
                                      {getInitials(currentUser?.display_name)}
                                    </AvatarFallback>
                                  </Avatar>
                                )}
                              </div>
                            );
                          })}
                        </CollapsibleContent>
                      </Collapsible>
                    ))
                  )}
                  <div ref={scrollRef} />
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="attachments" className="mt-4">
              <MessageAttachmentsPanel attachments={allAttachments} />
            </TabsContent>
          </Tabs>

          {/* Message Input */}
          <div className="space-y-2 border-t pt-4">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {selectedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 p-2 bg-muted rounded-md">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 bg-background px-3 py-1 rounded-md text-sm"
                  >
                    <Paperclip className="h-3 w-3" />
                    <span className="max-w-[150px] truncate">{file.name}</span>
                    <button
                      onClick={() => handleRemoveFile(index)}
                      className="text-destructive hover:text-destructive/80"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <Textarea
              placeholder="Type your message... (Press Enter to send, Shift+Enter for new line)"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={3}
              disabled={sending}
            />
            <div className="flex items-center justify-between">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleAttachClick}
                disabled={sending}
              >
                <Paperclip className="h-4 w-4 mr-2" />
                Attach File {selectedFiles.length > 0 && `(${selectedFiles.length})`}
              </Button>
              <Button
                onClick={handleSendMessage}
                disabled={(!newMessage.trim() && selectedFiles.length === 0) || sending}
                size="sm"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
